/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    DrmType,
    TransportEncodingType,
    VideoProviderConfiguration,
    VideoProviderConfigurationGetRequest,
    VideoProviderConfigurationsApi,
} from 'client-code/video-provider-configurations';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IVPConfigurationsServiceRepository } from '../interfaces/IVPConfigurationsServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { Storage } from '../storage/Storage';
import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceModel } from '../device/DeviceModel';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class VPConfigurationsServiceRepositoryImpl implements IVPConfigurationsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'VPConfigurationsServiceRepositoryImpl';

    private static supportedDrmType: Array<DrmType> = [];

    private static supportedTransportEncodingType: Array<TransportEncodingType> = [];

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    // TODO: Retry if drmServerUrl and serverSuppliedDeviceIdentifier not found in response
    private async getVPConfigurations(): Promise<void> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.PLAYER).headers;
            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: VideoProviderConfigurationGetRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: DeviceModel.getAppVersionName(),
                bodyId: this.mParams.bodyId,
                productName: DeviceModel.getTiVoSettings().getAppName(),
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
                supportedDrmType: VPConfigurationsServiceRepositoryImpl.supportedDrmType,
                supportedTransportEncodingType:
                    VPConfigurationsServiceRepositoryImpl.supportedTransportEncodingType,
            };

            const configuration = new Configuration(configurationParameters);
            const vpConfigurationsService = new VideoProviderConfigurationsApi(configuration);
            const vpConfigurationsResponse =
                await vpConfigurationsService.videoProviderConfigurationGet(requestParameters);

            Storage.getInstance().setItem(
                Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
                vpConfigurationsResponse.data,
            );
            Storage.getInstance().setItem(
                Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME,
                Date.now(),
            );
        } catch (error: any) {
            Log.e(this.TAG, 'getVPConfigurations', error);
            throw error;
        }
    }

    /**
     *
     * @param linearProviderPartnerId
     * @param forceNetworkCall(optional)
     * @returns VideoProviderConfiguration for provided LinearProviderPartnerId or Empty Array
     */
    async fetchVPConfigurationsForVideoPlayer(
        linearProviderPartnerId: string,
        forceNetworkCall?: boolean,
    ): Promise<Array<VideoProviderConfiguration>> {
        let vpConfigurationsData: Array<VideoProviderConfiguration> = Storage.getInstance().getItem(
            Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
        );
        try {
            if (forceNetworkCall) {
                await this.getVPConfigurations();
            } else {
                await this.checkAndUpdateCache();
            }

            vpConfigurationsData = Storage.getInstance().getItem(
                Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
            );

            if (!vpConfigurationsData.length) {
                return [];
            }
            const vpConfigurations = vpConfigurationsData.filter(
                (config) => config.providerPartnerId === linearProviderPartnerId,
            );

            return vpConfigurations;
        } catch (error: any) {
            const vpConfigurations = vpConfigurationsData.filter(
                (config) => config.providerPartnerId === linearProviderPartnerId,
            );
            error.data = vpConfigurations;
            throw error;
        }
    }

    /**
     * Function to check and update cached videoProviderConfigurations
     * @param supportedDrmType
     * @param supportedTransportEncodingType
     * @returns
     */
    async fetchVPConfigurations(
        supportedDrmType: Array<DrmType>,
        supportedTransportEncodingType: Array<TransportEncodingType>,
    ): Promise<void> {
        try {
            VPConfigurationsServiceRepositoryImpl.supportedDrmType = supportedDrmType;
            VPConfigurationsServiceRepositoryImpl.supportedTransportEncodingType =
                supportedTransportEncodingType;
            await this.checkAndUpdateCache();
        } catch (error) {
            Log.e(this.TAG, 'fetchVPConfigurations', error);
            throw error;
        }
    }

    private async checkAndUpdateCache() {
        try {
            const currentTime = Date.now();
            const vpConfigurationsLastCallTime = Storage.getInstance().getItem(
                Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME,
            );
            const vpConfigurationsData = Storage.getInstance().getItem(
                Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
            );
            const isServiceCallRequired: boolean =
                !vpConfigurationsLastCallTime ||
                currentTime >
                    vpConfigurationsLastCallTime +
                        CachingPolicy.VPConfigurationsService.cachingDuration;
            const isDataAvailable: boolean = vpConfigurationsData && vpConfigurationsData.length;

            if (isServiceCallRequired || !isDataAvailable) {
                await this.getVPConfigurations();
            }
        } catch (error: any) {
            Log.e(this.TAG, 'checkAndUpdateCache', error);
            throw error;
        }
    }
}
